import { useCallback, useRef, useState, type ReactNode } from 'react';
import { useCreatorCodeStatus } from '@discovr/features/shopify';
import { Badge } from '../../components/ui/badge';
import { ActionConfirmDialog } from '../../components/common/ActionConfirmDialog';

export const NO_CODE_DIALOG = {
  title: 'No discount code yet',
  description:
    "This creator doesn't have a discount code, so their brief will go out without one. Generate codes first in the Shopify tab, or send anyway.",
  confirm: 'Send anyway',
  // Staff can't generate codes (brand-only), so they're pointed at the brand.
  staffDescription:
    "This creator doesn't have a discount code, so their brief will go out without one. Ask the brand to generate this creator's code, or send anyway.",
} as const;

export function NoCodeChip({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <Badge variant="secondary">No code yet</Badge>
  );
}

interface NoCodeGuard {
  hasNoCode: (rosterEntryId: string) => boolean;
  /** Runs `send` now, or after the brand confirms when the creator has no code. */
  guardSend: (rosterEntryId: string, send: () => void | Promise<void>) => void;
  dialog: ReactNode;
}

export function useNoCodeBriefGuard(campaignId: string, enabled: boolean, staff = false): NoCodeGuard {
  const { hasNoCode } = useCreatorCodeStatus(campaignId, enabled);
  const [pending, setPending] = useState<(() => void | Promise<void>) | null>(null);
  const opener = useRef<HTMLElement | null>(null);

  const guardSend = useCallback<NoCodeGuard['guardSend']>(
    (rosterEntryId, send) => {
      if (hasNoCode(rosterEntryId)) {
        opener.current = document.activeElement as HTMLElement | null;
        setPending(() => send);
      } else void send();
    },
    [hasNoCode],
  );

  const dialog = (
    <ActionConfirmDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) setPending(null);
      }}
      variant="change"
      title={NO_CODE_DIALOG.title}
      description={staff ? NO_CODE_DIALOG.staffDescription : NO_CODE_DIALOG.description}
      confirmLabel={NO_CODE_DIALOG.confirm}
      hideConfirmIcon
      onCloseAutoFocus={(event) => {
        const target = opener.current;
        opener.current = null;
        if (!target?.isConnected) return;
        event.preventDefault();
        target.focus();
      }}
      onConfirm={() => {
        const send = pending;
        setPending(null);
        void send?.();
      }}
    />
  );

  return { hasNoCode, guardSend, dialog };
}
